import React, { useEffect, useState } from "react";
import { apiClient } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Copy, Share2, Gift, TrendingUp } from "lucide-react";

export default function ReferralCard() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    apiClient().get("/referral/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) return null;

  const url = `${window.location.origin}/signup?ref=${stats.code}`;
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Referral link copied"); };
  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Try Setu — the precision OS for construction sites",
          text: `Join me on Setubuild.OS. Use my code ${stats.code} and get an extra month free.`,
          url,
        });
      } catch {}
    } else copy();
  };

  return (
    <div className="border border-border bg-card p-5 space-y-4">
      <div className="flex items-center gap-2">
        <Gift className="h-4 w-4 text-primary" />
        <div className="font-heading text-lg">Refer & Earn</div>
      </div>
      <p className="text-sm text-muted-foreground">
        Share your code — you and your friend both get <strong className="text-primary">1 extra month FREE</strong> when they subscribe.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="border border-border p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Signups</div>
          <div className="font-display text-3xl mt-1">{stats.referred_count}</div>
        </div>
        <div className="border border-border p-3">
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Paying</div>
          <div className="font-display text-3xl mt-1 text-primary">{stats.converted_count}</div>
        </div>
      </div>

      <div>
        <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Your code</label>
        <div className="mt-1 border border-primary/30 bg-primary/5 p-3 font-display text-2xl tracking-widest" data-testid="referral-code">
          {stats.code}
        </div>
      </div>

      <div>
        <label className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Invite link</label>
        <div className="mt-1 flex gap-2">
          <input readOnly value={url} data-testid="referral-url"
            className="flex-1 border border-border bg-background px-3 py-2 text-xs mono" />
          <button onClick={copy} data-testid="referral-copy"
            className="btn-sharp inline-flex items-center gap-1.5 border border-border px-3 py-2 text-xs hover:bg-muted">
            <Copy className="h-3.5 w-3.5" /> Copy
          </button>
          <button onClick={share} data-testid="referral-share"
            className="btn-sharp inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-3 py-2 text-xs">
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </div>

      {stats.signups.length > 0 && (
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
            <TrendingUp className="h-3 w-3" /> Recent signups
          </div>
          <ul className="space-y-1">
            {stats.signups.slice(0, 5).map((s, i) => (
              <li key={i} className="text-xs border border-border p-2 flex items-center justify-between">
                <span>{s.name}</span>
                <span className={`mono ${s.subscription?.status === "active" ? "text-primary" : "text-muted-foreground"}`}>
                  {s.subscription?.plan?.toUpperCase() || "TRIAL"}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
